import { useState, useEffect, useCallback, useRef } from 'react';
import { authService } from '@/lib/authService';
import { buildApiUrl, isAppShellRoute } from '@/lib/platformUtils';

/**
 * Hook to check if user has access to specific modules
 * Used for conditional UI rendering based on module subscriptions
 * 
 * Example usage:
 * const { hasAccess, loading } = useModuleAccess(['total-connect', 'total-pabx']);
 * 
 * // Check specific module
 * if (hasAccess('total-pabx')) { ... }
 * 
 * // Get call modal mode
 * const mode = getCallModalMode(); // 'pabx-only' | 'connect-only' | 'full' | 'none'
 */

export interface ModuleAccessInfo {
  module_key: string;
  module_name: string;
  status: string;
  role: string;
  has_sip_extension?: boolean;
}

export interface UseModuleAccessReturn {
  /** Check if user has access to a specific module */
  hasAccess: (moduleKey: string) => boolean;
  /** All modules the user has access to */
  modules: ModuleAccessInfo[];
  /** Loading state */
  loading: boolean;
  /** Error message if any */
  error: string | null;
  /** Refresh module access data */
  refresh: () => Promise<void>;
  /** Get the appropriate call modal mode based on module access */
  getCallModalMode: () => 'pabx-only' | 'connect-only' | 'full' | 'none';
  /** Check if user has any telephony capability */
  hasTelephony: boolean;
  /** Check if user has internal calling (total-connect) */
  hasInternalCalling: boolean;
  /** Check if user has PABX/external calling */
  hasExternalCalling: boolean;
}

// Cache module access globally to prevent redundant API calls
let moduleAccessCache: ModuleAccessInfo[] | null = null;
let cacheTimestamp: number = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export function useModuleAccess(moduleKeys?: string[]): UseModuleAccessReturn {
  const [modules, setModules] = useState<ModuleAccessInfo[]>(moduleAccessCache || []);
  const [loading, setLoading] = useState(!moduleAccessCache);
  const [error, setError] = useState<string | null>(null);
  const fetchedRef = useRef(false);

  const fetchModuleAccess = useCallback(async (force = false) => {
    // Use cache if valid and not forcing refresh
    const now = Date.now();
    if (!force && moduleAccessCache && (now - cacheTimestamp) < CACHE_TTL) {
      setModules(moduleAccessCache);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Module access is app-shell data — skip on public pages so mounted
      // always-on components (e.g. TotalConnectRTC) don't 401 there.
      if (typeof window !== 'undefined' && !isAppShellRoute(window.location.pathname)) {
        setLoading(false);
        return;
      }

      const tokens = authService.getTokens();
      if (!tokens?.access_token) {
        setError('Not authenticated');
        setLoading(false);
        return;
      }

      const url = buildApiUrl('/api/v1/module-access');
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${tokens.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch module access');
      }

      const data = await response.json();
      
      if (data.success && data.module_access) {
        const accessList: ModuleAccessInfo[] = (data.module_access as Array<{
          module_key: string;
          module_name?: string;
          status: string;
          role: string;
          has_sip_extension?: boolean;
        }>).map((m) => ({
          module_key: m.module_key,
          module_name: m.module_name || m.module_key,
          status: m.status,
          role: m.role,
          has_sip_extension: m.has_sip_extension
        }));

        // Update cache
        moduleAccessCache = accessList;
        cacheTimestamp = now;

        setModules(accessList);
      }
    } catch (err) {
      console.error('[useModuleAccess] Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load module access');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    if (!fetchedRef.current) {
      fetchedRef.current = true;
      fetchModuleAccess();
    }
  }, [fetchModuleAccess]);

  // Check if user has access to a specific module
  const hasAccess = useCallback((moduleKey: string): boolean => {
    return modules.some(m => m.module_key === moduleKey && m.status === 'active');
  }, [modules]);

  // Determine call modal mode based on module access
  const getCallModalMode = useCallback((): 'pabx-only' | 'connect-only' | 'full' | 'none' => {
    const hasPabx = hasAccess('total-pabx');
    const hasConnect = hasAccess('total-connect');
    
    if (hasPabx && hasConnect) return 'full';
    if (hasPabx && !hasConnect) return 'pabx-only';
    if (!hasPabx && hasConnect) return 'connect-only';
    return 'none';
  }, [hasAccess]);

  // Derived states
  const hasTelephony = hasAccess('total-pabx') || hasAccess('total-connect');
  const hasInternalCalling = hasAccess('total-connect');
  const hasExternalCalling = hasAccess('total-pabx');

  return {
    hasAccess,
    modules,
    loading,
    error,
    refresh: () => fetchModuleAccess(true),
    getCallModalMode,
    hasTelephony,
    hasInternalCalling,
    hasExternalCalling
  };
}

/**
 * Clear module access cache (call on logout or client switch)
 */
export function clearModuleAccessCache() {
  moduleAccessCache = null;
  cacheTimestamp = 0;
}

export default useModuleAccess;
